import { llamaLendChainData, llamaLendConfigFor } from '@1delta/data-sdk'
import {
  asInt256,
  decodeLensRows,
  LensRow,
} from '../position-lens'
import { parseRawAmount } from '../../../utils/parsing'
import { isFailedCall } from '../../../utils/multicall'
import { createMarketUid } from '../../../utils'
import { getDisplayPrice, getOraclePrice } from '../utils/oraclePrice'
import { createMultiAccountTypeUserState } from '../utils'
import {
  LenderCrossPoolMeta,
  LenderToLenderCrossPoolMeta,
  UserData,
} from '../utils/types'
import { llamaLendLenderKey } from '../../public-data/llamalend/convertPublic'
import {
  LLAMALEND_CALLS_PER_MARKET,
  LLAMALEND_LENS_WORDS,
  usesLlamaLendLens,
} from './userCallBuild'

/**
 * One market's raw user state, however it was read.
 *
 * The two read paths — the 8-call roster walk and the single `PositionLens` call —
 * differ only in how these fields are obtained. Everything downstream (pricing,
 * row construction, the `llamalendInfo` payload) is shared, so the two paths cannot
 * drift into reporting different positions for the same chain.
 */
interface LlamaLendMarketState {
  collateral: bigint
  bandBorrowed: bigint
  debt: bigint
  bandCount: number
  health: bigint
  priceUpper: bigint
  priceLower: bigint
  bands: [number, number] | undefined
  delegated: boolean
  vaultShares: bigint
  gaugeShares: bigint
  shareToAsset: bigint
}

/** Roster-walk layout: 8 consecutive slots per market, in build order. */
const statesFromSlice = (
  data: any[],
  marketCount: number,
): Map<number, LlamaLendMarketState> => {
  const out = new Map<number, LlamaLendMarketState>()
  for (let i = 0; i < marketCount; i++) {
    const base = i * LLAMALEND_CALLS_PER_MARKET
    const stateRaw = data[base]
    // A failed `user_state` is not an empty position — skip the market rather
    // than reporting the user as debt-free.
    if (isFailedCall(stateRaw) || !Array.isArray(stateRaw)) continue

    const ticksRaw = data[base + 3]
    const approvalRaw = data[base + 4]
    const vaultSharesRaw = data[base + 5]
    const gaugeSharesRaw = data[base + 6]
    const shareToAssetRaw = data[base + 7]
    const healthRaw = data[base + 1]
    const pricesRaw = data[base + 2]

    out.set(i, {
      collateral: big(stateRaw[0]),
      bandBorrowed: big(stateRaw[1]),
      debt: big(stateRaw[2]),
      bandCount: Number(big(stateRaw[3])),
      health: isFailedCall(healthRaw) ? 0n : big(healthRaw),
      priceUpper: arrAt(pricesRaw, 0),
      priceLower: arrAt(pricesRaw, 1),
      bands:
        isFailedCall(ticksRaw) || !Array.isArray(ticksRaw)
          ? undefined
          : [Number(big(ticksRaw[0])), Number(big(ticksRaw[1]))],
      delegated: isFailedCall(approvalRaw) ? false : approvalRaw === true,
      vaultShares: isFailedCall(vaultSharesRaw) ? 0n : big(vaultSharesRaw),
      gaugeShares: isFailedCall(gaugeSharesRaw) ? 0n : big(gaugeSharesRaw),
      shareToAsset: isFailedCall(shareToAssetRaw) ? 0n : big(shareToAssetRaw),
    })
  }
  return out
}

/**
 * Lens layout: one packed blob, one row per market the account is actually in.
 *
 * A market ABSENT from the rows is an answer — the lens probed it and found
 * nothing — which is why this returns only what it was given and never
 * back-fills. A FAILED lens call is a different thing entirely and is handled
 * before we get here: the call is the family's only slot, so `allFailed` in
 * `convertLenderUserDataResult` drops the family and reports it, rather than
 * letting an empty map read as "no positions anywhere".
 */
const statesFromLens = (rows: LensRow[]): Map<number, LlamaLendMarketState> => {
  const out = new Map<number, LlamaLendMarketState>()
  for (const r of rows) {
    const w = r.words
    out.set(r.index, {
      collateral: w[0],
      bandBorrowed: w[1],
      debt: w[2],
      bandCount: Number(w[3]),
      health: asInt256(w[4]),
      priceUpper: w[5],
      priceLower: w[6],
      bands: [Number(asInt256(w[7])), Number(asInt256(w[8]))],
      // bit0 — the spender holds this Controller's boolean grant. Absent on
      // older-blueprint controllers, where the lens's read fails and this reads
      // false: fail closed, same as the roster walk.
      delegated: (r.flags & 1) !== 0,
      vaultShares: w[9],
      gaugeShares: w[10],
      shareToAsset: w[11],
    })
  }
  return out
}

/**
 * Per-position LlamaLend detail attached to the debt row (raw strings unless
 * noted).
 *
 * The two fields a consumer must not ignore:
 *
 *  - `softLiquidating` / `priceUpper` / `priceLower` — a LlamaLend position
 *    does not have a liquidation price. It has a band RANGE, and it starts
 *    converting collateral into the borrowed token as soon as the oracle
 *    enters that range. Rendering a single number here misrepresents the
 *    protocol.
 *  - `bandCollateralInBorrowed` — the borrowed-token leg the LLAMMA has
 *    already produced from the user's collateral. It sits inside the position,
 *    offsets debt, and is NOT a wallet balance.
 */
export interface LlamaLendPositionInfo {
  /** Signed health, WAD. `< 0` ⇒ hard-liquidatable. */
  health: string
  /** Upper bound of the soft-liquidation band range, WAD. */
  priceUpper: string
  /** Lower bound of the soft-liquidation band range, WAD. */
  priceLower: string
  /** Band indices `[n1, n2]` the collateral currently occupies. */
  bands: [number, number] | undefined
  /** Band count `N` chosen at loan creation — IMMUTABLE for the loan's life. */
  bandCount: number
  /**
   * Borrowed-token amount held inside the user's bands (raw). Non-zero means
   * the position IS or HAS BEEN in soft liquidation.
   */
  bandCollateralInBorrowed: string
  /**
   * The user's SUPPLY position on this market, in the borrowed token — vault
   * shares plus gauge-staked shares, converted to assets.
   *
   * Separate from the row's `deposits`, which sums this with
   * `bandCollateralInBorrowed`. Only this part earns the vault's lend APR.
   */
  lendAssets: string
  /** Raw lend SHARES (vault + gauge). ~1000x the asset scale — never an amount. */
  lendShares: string
  /** True when some or all of the lend shares are staked in the market's gauge. */
  lendStaked: boolean
  /** True when the LLAMMA currently holds a borrowed-token leg for this user. */
  softLiquidating: boolean
  /**
   * Whether the probed spender holds the Controller's boolean grant for this
   * market. FAILS CLOSED — older-blueprint controllers have no `approval`
   * method, the call fails, and this reads `false`, which is correct.
   */
  delegated: boolean
  /** Whether the market's Controller supports delegation at all. */
  supportsDelegation: boolean
  /** Market generation — the leverage/encoder ABIs differ. */
  version: 1 | 2
}

const big = (v: any): bigint => {
  try {
    if (typeof v === 'bigint') return v
    if (typeof v === 'number' || typeof v === 'string') return BigInt(v)
  } catch {}
  return 0n
}

/** Read element `i` of a fixed-size array return, tolerating call failure. */
const arrAt = (v: any, i: number): bigint => {
  if (isFailedCall(v) || !Array.isArray(v)) return 0n
  return big(v[i])
}

/**
 * Returns a [converter, expectedCallCount] tuple for LlamaLend user data.
 *
 * Layout mirrors `buildLlamaLendUserCall` (deterministic from the roster — no
 * discovery). Emits one `UserData` per market key
 * `LLAMALEND_<CONTROLLER_ADDR>`, each with a single sub-account `'0'` —
 * positions are address-keyed and the Controller allows at most one loan per
 * address per market, so unlike Liquity/Teller there is nothing to enumerate.
 *
 * Rows per market: a collateral row (the collateral still sitting in bands)
 * and a debt row carrying `llamalendInfo`. When the position is in soft
 * liquidation the borrowed-token leg produced by the LLAMMA is added to the
 * DEBT row's `deposits` rather than invented as a separate market — it is
 * value the user holds inside the same position and it nets against debt.
 */
export const getLlamaLendUserDataConverter = (
  lender: string,
  chainId: string,
  account: string,
  meta?: LenderToLenderCrossPoolMeta,
): [(data: any[]) => { [lender: string]: UserData } | undefined, number] => {
  const cfg = llamaLendConfigFor(lender, chainId)
  const markets = llamaLendChainData(lender, chainId)?.markets ?? []
  const viaLens = usesLlamaLendLens(chainId)
  const expected =
    cfg && markets.length > 0
      ? viaLens
        ? 1
        : markets.length * LLAMALEND_CALLS_PER_MARKET
      : 0

  return [
    (data: any[]) => {
      if (!cfg || expected === 0) return undefined
      if (!data || data.length < expected) return undefined

      const out: { [lender: string]: UserData } = {}

      let states: Map<number, LlamaLendMarketState>
      if (viaLens) {
        const blobs = data[0]
        // `getUserData` returns `bytes[]`; one query in, one blob out. Anything
        // else is a contract/ABI disagreement, not an empty position set.
        if (isFailedCall(blobs) || !Array.isArray(blobs) || blobs.length !== 1) {
          return undefined
        }
        states = statesFromLens(
          decodeLensRows(blobs[0], LLAMALEND_LENS_WORDS).rows,
        )
      } else {
        states = statesFromSlice(data, markets.length)
      }

      markets.forEach((market, i) => {
        const st = states.get(i)
        // Absent means one of two things, and both end here: the roster walk's
        // read failed (skip, rather than report the user as debt-free), or the
        // lens probed the market and found nothing.
        if (!st) return

        const { collateral, bandBorrowed, debt, bandCount } = st

        /**
         * The LEND side. Shares live in the vault OR staked in the market's
         * gauge, 1:1, so a lender's holding is the sum — and the gauge is where
         * most of it sits (91-99.7 % of supply on the Ethereum markets that use
         * one), which is why reading the vault alone showed almost no lenders.
         */
        const { vaultShares, gaugeShares } = st
        const lendShares = vaultShares + gaugeShares

        // Nothing on either side of the market — not a borrower, not a lender.
        if (
          collateral === 0n &&
          debt === 0n &&
          bandBorrowed === 0n &&
          lendShares === 0n
        )
          return

        const { health, priceUpper, priceLower, bands, delegated } = st

        const lenderKey = llamaLendLenderKey(lender, market.controller)
        const metaMap: LenderCrossPoolMeta | undefined = meta?.[lenderKey]
        if (!metaMap) return

        const collAddr = market.collateralToken.toLowerCase()
        const loanAddr = market.borrowedToken.toLowerCase()
        const collUid = createMarketUid(chainId, lenderKey, collAddr)
        const loanUid = createMarketUid(chainId, lenderKey, loanAddr)
        const collMeta = metaMap[collUid]
        const loanMeta = metaMap[loanUid]
        const collDecimals =
          collMeta?.asset?.decimals ?? market.collateralDecimals
        const loanDecimals =
          loanMeta?.asset?.decimals ?? market.borrowedDecimals
        const collDisplay = collMeta ? getDisplayPrice(collMeta) : 0
        const collOracle = collMeta ? getOraclePrice(collMeta) : 0
        const collHist = collMeta?.price?.priceUsd24h ?? collDisplay
        const loanDisplay = loanMeta ? getDisplayPrice(loanMeta) : 0
        const loanOracle = loanMeta ? getOraclePrice(loanMeta) : 0
        const loanHist = loanMeta?.price?.priceUsd24h ?? loanDisplay

        /**
         * Lend shares → assets, via the share price read in the SAME multicall.
         *
         * `convertToAssets(1e18)` answers "what is 1e18 shares worth", so the
         * conversion is `shares * price / 1e18`. Done in bigint to keep full
         * precision at the raw scale before the single float conversion below.
         *
         * A failed price read yields ZERO rather than falling back to a 1:1
         * assumption: shares run ~1000x the asset scale, so treating them as
         * assets would overstate a lender's position roughly a thousandfold.
         * Reporting nothing is wrong by omission; reporting 1000x is wrong in a
         * way that would propagate into net worth and health.
         */
        const { shareToAsset } = st
        const lendAssets =
          shareToAsset === 0n ? 0n : (lendShares * shareToAsset) / 10n ** 18n
        const lendStr = parseRawAmount(lendAssets.toString(), loanDecimals)
        const lendNum = Number(lendStr)

        /**
         * The loan row's deposit is the lend position PLUS the soft-liquidated
         * leg — both are the borrowed token held on this market, so they share
         * a `marketUid` and have to be summed to give an honest total.
         *
         * They are not the same product, and the split is published on
         * `llamalendInfo` so a UI can say which is which: `lendAssets` earns
         * the vault's lend APR, while `bandCollateralInBorrowed` sits inside
         * the LLAMMA earning nothing and offsetting debt. A consumer applying
         * the row's deposit APR to the whole balance slightly overstates the
         * yield on a position that is simultaneously lending and mid-soft-
         * liquidation — rare, and recoverable from the split.
         */
        const depositStr = parseRawAmount(
          (lendAssets + bandBorrowed).toString(),
          loanDecimals,
        )
        const depositNum = Number(depositStr)

        const llamalendInfo: LlamaLendPositionInfo = {
          health: health.toString(),
          priceUpper: priceUpper.toString(),
          priceLower: priceLower.toString(),
          bands,
          bandCount,
          bandCollateralInBorrowed: bandBorrowed.toString(),
          lendAssets: lendStr,
          lendShares: lendShares.toString(),
          lendStaked: gaugeShares > 0n,
          softLiquidating: bandBorrowed > 0n,
          delegated,
          supportsDelegation: market.supportsDelegation === true,
          version: market.version,
        }

        const collStr = parseRawAmount(collateral.toString(), collDecimals)
        const collNum = Number(collStr)
        const debtStr = parseRawAmount(debt.toString(), loanDecimals)
        const debtNum = Number(debtStr)
        // The soft-liquidated leg is denominated in the BORROWED token and
        // lives on the debt row, where it reads as a deposit against that
        // debt — which is exactly what it is economically.
        const bandBorrowedStr = parseRawAmount(
          bandBorrowed.toString(),
          loanDecimals,
        )
        const bandBorrowedNum = Number(bandBorrowedStr)

        const lendingPositions: Record<string, any> = {
          '0': {
            [collUid]: {
              marketUid: collUid,
              underlying: collAddr,
              deposits: collStr,
              debt: '0',
              debtStable: '0',
              depositsUSD: collNum * collDisplay,
              debtUSD: 0,
              debtStableUSD: 0,
              depositsUSDOracle: collNum * collOracle,
              debtUSDOracle: 0,
              debtStableUSDOracle: 0,
              stableBorrowRate: '0',
              collateralEnabled: true,
              claimableRewards: 0,
            },
            [loanUid]: {
              marketUid: loanUid,
              underlying: loanAddr,
              deposits: depositStr,
              debt: debtStr,
              debtStable: '0',
              depositsUSD: depositNum * loanDisplay,
              debtUSD: debtNum * loanDisplay,
              debtStableUSD: 0,
              depositsUSDOracle: depositNum * loanOracle,
              debtUSDOracle: debtNum * loanOracle,
              debtStableUSDOracle: 0,
              stableBorrowRate: '0',
              // The borrowed token is never collateral in a LlamaLend market —
              // supplying it earns the lend rate, it does not back a loan.
              collateralEnabled: false,
              claimableRewards: 0,
              llamalendInfo,
            },
          },
        }
        /**
         * The position's OWN band count — the value the config's
         * `openParameter` describes the domain of.
         *
         * LlamaLend has no sub-accounts (`create_loan` asserts
         * `initial_debt == 0` on both generations), so there is exactly one
         * slot, keyed `'0'`. It used to carry a hardcoded 0, which read as
         * "e-mode 0" and left consumers deriving LTV/health from the config's
         * default-band number instead — wrong for every borrower not at the
         * default, ~21% of live loans when sampled.
         *
         * Interpretation comes from `config.openParameter.kind`
         * (`llamalend-bands`); a bare number here would otherwise render as an
         * e-mode category.
         */
        const modes: Record<string, number> = { '0': bandCount }
        // Must use the SAME deposit basis as the row above (lend + soft-liq
        // leg). Leaving the band leg alone here would make a pure lender's
        // whole position look newly added on every cycle.
        const hist = {
          '0': {
            totalDeposits24h: collNum * collHist + depositNum * loanHist,
            totalDebt24h: debtNum * loanHist,
          },
        }

        const userData = createMultiAccountTypeUserState(
          { chainId, account, lendingPositions, modes } as any,
          metaMap,
          hist,
        )
        if (userData && userData.data.length > 0) out[lenderKey] = userData
      })

      return Object.keys(out).length > 0 ? out : undefined
    },
    expected,
  ]
}
